import { useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useWindowSize } from '@/hooks/useWindowSize';
import { useSwipe } from '@/hooks/useSwipe';
import IconStars from '@/assets/icons/icon_stars_20.svg?react';
import IconChevronDown from '@/assets/icons/icon_chevron_down_20.svg?react';
import IconChevronUp from '@/assets/icons/icon_chevron_up_20.svg?react';
import IconEyeOff from '@/assets/icons/icon_eye_off_20.svg?react';
import IconFlame from '@/assets/icons/icon_flame_16.svg?react';
import s from './index.module.scss';

export function InvestorDecisionWidgetView({
  isCollapsed,
  isDismissed,
  collapsedTitle,
  titleNode,
  messageNode,
  secondaryNode,
  tooltipNode,
  ctaNode,
  onToggleCollapse,
  onDismiss,
}) {
  const { isMobile } = useWindowSize();
  const swipeRef = useRef(null);
  const frameRef = useRef(null);

  useSwipe(swipeRef, () => onToggleCollapse(), {
    sensitivity: 30,
    axis: 'vertical',
  });

  useEffect(() => {
    if (isCollapsed || isMobile) return undefined;
    const frame = frameRef.current;
    if (!frame) return undefined;

    const updateOffset = () => {
      document.documentElement.style.setProperty(
        '--decision-widget-chat-offset',
        `${frame.offsetHeight - 10}px`,
      );
    };

    updateOffset();
    document.body.classList.add('decision-widget-expanded');

    const observer = new ResizeObserver(updateOffset);
    observer.observe(frame);

    return () => {
      observer.disconnect();
      document.body.classList.remove('decision-widget-expanded');
      document.documentElement.style.removeProperty('--decision-widget-chat-offset');
    };
  }, [isCollapsed, isMobile]);

  if (isDismissed) return null;

  if (isCollapsed) {
    return (
      <div className={s.overlay}>
        <div className={classNames(s.frame, s.frame_collapsed)}>
          <div className={classNames(s.inner, s.inner_collapsed)}>
            <IconStars className={s.title_icon} />
            <span className={classNames(s.title, s.title_collapsed)}>{collapsedTitle}</span>
            <div className={s.collapsed_actions}>
              <button
                type="button"
                className={classNames(s.icon_button, s.icon_button_ghost)}
                onClick={onDismiss}
              >
                <IconEyeOff />
              </button>
              <button
                type="button"
                className={classNames(s.icon_button, s.icon_button_ghost)}
                onClick={onToggleCollapse}
              >
                <IconChevronUp />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className={s.overlay}>
        <div className={classNames(s.frame, s.frame_expanded, s.frame_mobile)}>
          <button
            type="button"
            ref={swipeRef}
            className={s.sheet_handle_btn}
            onClick={onToggleCollapse}
            aria-label={collapsedTitle}
          />
          <div className={classNames(s.inner, s.inner_expanded, s.inner_mobile)}>
            <div className={s.mobile_content}>
              <div className={s.mobile_text_section}>
                <div className={s.mobile_title_row}>
                  <div className={s.mobile_title_with_icon}>
                    <IconStars className={s.title_icon} />
                    <span className={s.title}>{titleNode}</span>
                  </div>
                  {tooltipNode}
                </div>
                <div className={s.message}>{messageNode}</div>
              </div>
              <div className={s.mobile_actions}>{ctaNode}</div>
            </div>
            {secondaryNode && (
              <div className={classNames(s.secondary, s.mobile_secondary)}>
                <IconFlame className={s.secondary_icon} />
                <span>{secondaryNode}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={s.overlay}>
      <div className={classNames(s.frame, s.frame_expanded)} ref={frameRef}>
        <div className={classNames(s.inner, s.inner_expanded)}>
          <div className={s.content_row}>
            <div className={s.text_block}>
              <div className={s.title_row}>
                <IconStars className={s.title_icon} />
                <span className={s.title}>{titleNode}</span>
              </div>
              <div className={s.message}>{messageNode}</div>
            </div>
            {tooltipNode && <div className={s.tooltip_slot}>{tooltipNode}</div>}
            <div className={s.actions}>
              {ctaNode}
              <button
                type="button"
                className={s.icon_button}
                onClick={onToggleCollapse}
              >
                <IconChevronDown />
              </button>
            </div>
          </div>
          {secondaryNode && (
            <>
              <div className={s.divider} />
              <div className={s.secondary}>
                <IconFlame className={s.secondary_icon} />
                <span>{secondaryNode}</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}